/**
 * Loads service items for the selected regional service.
 */

import { useEffect, useState } from "react";
import { endpoints } from "../../../configs/config";
import { REGIONAL_SELECTION_DEFAULTS } from "configs/config";
import { normalize } from "../constants";

export default function useServiceItemOptions({ service, allowedEntityNames }) {
  const [serviceItems, setServiceItems] = useState([]);
  const [loadingServiceItems, setLoadingServiceItems] = useState(false);
  const [serviceItemsError, setServiceItemsError] = useState(null);

  useEffect(() => {
    setServiceItems([]);
    setServiceItemsError(null);

    if (!service) return;

    const controller = new AbortController();
    setLoadingServiceItems(true);

    fetch(endpoints.serviceItemsUrl(service.Id), { signal: controller.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        let sortedItems = (data.Items || [])
          .map((item) => ({
            ...item,
            displayLabel:
              item.Optional && String(item.Optional).trim()
                ? `${item.Name} - ${String(item.Optional).trim()}`
                : `${item.Name}`,
          }))
          .sort((a, b) => {
            const aStr = normalize(a.displayLabel);
            const bStr = normalize(b.displayLabel);
            if (aStr < bStr) return -1;
            if (aStr > bStr) return 1;
            return 0;
          });

        // Apply filter if allowedEntityNames is provided AND the selected
        // source matches the configured default source (e.g. 'Bundesland').
        // This prevents filtering items for other sources where the
        // allowedEntityNames list is not relevant.
        if (
          allowedEntityNames &&
          allowedEntityNames.length > 0 &&
          REGIONAL_SELECTION_DEFAULTS.source &&
          normalize(service.Name) ===
            normalize(REGIONAL_SELECTION_DEFAULTS.source)
        ) {
          const normalizedAllowed = allowedEntityNames.map(normalize);
          sortedItems = sortedItems.filter((item) =>
            normalizedAllowed.includes(normalize(item.displayLabel)),
          );
        }

        setServiceItems(sortedItems);
        setLoadingServiceItems(false);
        setServiceItemsError(null);
      })
      .catch((error) => {
        if (error.name === "AbortError") return;
        setLoadingServiceItems(false);
        setServiceItemsError(error.message);
      });

    return () => controller.abort();
    // Match legacy trigger: fetch on service changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service]);

  return {
    serviceItems,
    loadingServiceItems,
    serviceItemsError,
    setServiceItems,
  };
}
